/* eslint-disable react/no-unused-prop-types, camelcase */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import Slider from 'react-rangeslider';
import { isEqual, isEmpty } from 'lodash';
import Image from '../../components/Image';
import RichTextDisplay from '../../components/RichTextDisplay';
import WorkspaceSlideHeader from './WorkspaceSlideHeader';

import TrackPlayerSlideComponent from '../../hocs/TrackPlayerSlideComponent';

const emotionImages = [
    {
        normal: '/static/images/likert/Likert_Extremely_Unhappy.png',
        hover: '/static/images/likert/Likert_Extremely_Unhappy_Hover.png'
    },
    {
        normal: '/static/images/likert/Likert_Unhappy.png',
        hover: '/static/images/likert/Likert_Unhappy_Hover.png'
    },
    {
        normal: '/static/images/likert/Likert_Neutral.png',
        hover: '/static/images/likert/Likert_Neutral_Hover.png'
    },
    {
        normal: '/static/images/likert/Likert_Happy.png',
        hover: '/static/images/likert/Likert_Happy_Hover.png'
    },
    {
        normal: '/static/images/likert/Likert_Extremely_Happy.png',
        hover: '/static/images/likert/Likert_Extremely_Happy_Hover.png'
    }
];

const typeConfig = {
    radio: {
        component: (props) => {
            const {
                onClick,
                option: { score }
            } = props;
            return (
                <div
                    className={'likertSlide-optionRadio'}
                    data-score={score}
                    onClick={onClick}
                    onKeyPress={onClick}
                    tabIndex={'0'}
                />
            );
        }
    },
    image: {
        component: (props) => {
            const {
                option: { score, image },
                onClick
            } = props;
            return (
                <div
                    className={'likertSlide-optionImage'}
                    data-score={score}
                    onClick={onClick}
                    onKeyPress={onClick}
                    tabIndex={'0'}
                >
                    <Image src={image} alt={`Option ${score}`} />
                </div>
            );
        }
    },
    stars: {
        component: (props) => {
            const {
                onClick,
                option: { score },
                hoveredScore,
                selectedScore,
                onMouseEnter,
                onMouseLeave
            } = props;
            let imageSrc = '/static/images/likert/Likert_Star_Empty.png';
            if (
                ((selectedScore || selectedScore === 0) && score <= selectedScore) ||
                ((hoveredScore || hoveredScore === 0) && score <= hoveredScore)
            ) {
                imageSrc = '/static/images/likert/Likert_Star_Filled.png';
            }
            return (
                <div
                    className={'likertSlide-optionStar'}
                    data-score={score}
                    onClick={onClick}
                    onKeyPress={onClick}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    tabIndex={'0'}
                >
                    <Image src={imageSrc} alt={`Option ${score}`} />
                </div>
            );
        }
    },
    emotion: {
        component: (props) => {
            const {
                onClick,
                option: { score },
                idx,
                hoveredScore,
                selectedScore,
                onMouseEnter,
                onMouseLeave
            } = props;
            let imageSrc = emotionImages[idx] && emotionImages[idx].normal;
            if (score === selectedScore || score === hoveredScore) {
                imageSrc = emotionImages[idx].hover;
            }
            return (
                <div
                    className={'likertSlide-optionEmotion'}
                    data-score={score}
                    onClick={onClick}
                    onKeyPress={onClick}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    tabIndex={'0'}
                >
                    <Image src={imageSrc} alt={`Option ${score}`} />
                </div>
            );
        }
    },
    slider: {
        // This input is done via custom slider component
        component: () => null
    }
};
typeConfig.emotion.component.propTypes = {
    onClick: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    option: PropTypes.object,
    idx: PropTypes.number,
    hoveredScore: PropTypes.number,
    selectedScore: PropTypes.number
};
typeConfig.stars.component.propTypes = {
    onClick: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    option: PropTypes.object,
    hoveredScore: PropTypes.number,
    selectedScore: PropTypes.number
};
typeConfig.radio.component.propTypes = {
    onClick: PropTypes.func,
    option: PropTypes.object
};
typeConfig.image.component.propTypes = {
    onClick: PropTypes.func,
    option: PropTypes.object
};

class LikertSlide extends Component {
    static propTypes = {
        completedData: PropTypes.object,
        isUpdatingUserTrack: PropTypes.bool,
        onChange: PropTypes.func,
        content: PropTypes.shape({
            displayType: PropTypes.string,
            description: PropTypes.string,
            options: PropTypes.array,
            start: PropTypes.number,
            length: PropTypes.number,
            includeScoreLabels: PropTypes.bool
        }).isRequired,
        name: PropTypes.string.isRequired,
        canMoveToNext: PropTypes.func,
        currentUser: PropTypes.object.isRequired,
        nextSectionName: PropTypes.string,
        isRequired: PropTypes.bool,
        sectionId: PropTypes.string,
        sectionItemId: PropTypes.string,
        hideBookmark: PropTypes.bool,
        sendWorkspaceSegementEvent: PropTypes.func,
        isRefSheet: PropTypes.bool,
    };

    constructor(props) {
        super(props);
        this.state = {
            selectedScore: props.completedData ? props.completedData.data : null,
            hoveredScore: null
        };
    }

    UNSAFE_componentWillMount() {
        const { onChange, completedData = {}, isRequired = false } = this.props;
        if (completedData) {
            this.setState({
                selectedScore: completedData.data
            });
        }

        if (this.props.canMoveToNext) {
            const isFilled = Boolean(this.state.selectedScore || this.state.selectedScore === 0);
            this.props.canMoveToNext(isFilled || !isRequired);
        }
        if (!isRequired && isEmpty(completedData)) {
            const isCompleted = true;
            const isAnswered = false;
            onChange(null, isCompleted, isAnswered);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!isEqual(nextProps.completedData, this.props.completedData)) {
            this.setState({
                selectedScore: nextProps.completedData.data
            });
        }
    }

    componentWillUnmount() {
        this.setState({
            selectedScore: null,
            hoveredScore: null
        });
    }

    /**
     *
     * @param score
     */
    handleOptionSelect = (score) => {
        if (score === this.state.selectedScore) {
            return;
        }

        this.setState({
            selectedScore: score
        });
        if (this.props.onChange) {
            this.props.onChange(score, Boolean(score || score === 0), true);
        }
        if (!this.props.isUpdatingUserTrack) {
            this.props.canMoveToNext(true);
        }
    };

    /**
     *
     * @param {Object} e
     * @param {Object} e.currentTarget
     */
    handleOptionMouseEnter = (e) => {
        this.setState({
            hoveredScore: parseInt(e.currentTarget.getAttribute('data-score'), 10)
        });
    };

    /**
     *
     * @param {Object} e
     */
    handleOptionMouseLeave = () => {
        this.setState({
            hoveredScore: null
        });
    };

    getOption = (option, idx) => {
        let {
            content: { displayType }
        } = this.props;
        const {
            content: { includeScoreLabels }
        } = this.props;
        const { selectedScore, hoveredScore } = this.state;
        const isSelected = selectedScore === option.score;
        if (!typeConfig[displayType]) {
            displayType = 'radio';
        }
        const OptionComponent = typeConfig[displayType].component;
        const className = cns('likertSlide-option', {
            'likertSlide-option-hasImage': displayType === 'image',
            'likertSlide-option-selected': isSelected,
            'likertSlide-option-includeScore': includeScoreLabels,
            'likertSlide-verticalMobile': displayType === 'radio'
        });
        return (
            <div className={className} key={option.score}>
                <div
                    className={cns('likertSlide-option-component-container', {
                        'likertSlide-verticalMobile': displayType === 'radio'
                    })}
                >
                    <OptionComponent
                        option={option}
                        onClick={() => this.handleOptionSelect(option.score)}
                        onMouseEnter={this.handleOptionMouseEnter}
                        onMouseLeave={this.handleOptionMouseLeave}
                        selectedScore={selectedScore}
                        idx={idx}
                        hoveredScore={hoveredScore}
                    />
                </div>
                <div
                    className={cns('likertSlide-optionScore', {
                        'likertSlide-verticalMobile': displayType === 'radio'
                    })}
                >
                    {option.score}
                </div>
                <div
                    className={cns('likertSlide-optionLabel', {
                        'likertSlide-verticalMobile': displayType === 'radio'
                    })}
                >
                    {(option.label || '').toLocaleUpperCase()}
                </div>
            </div>
        );
    };

    getSlider = () => {
        const { selectedScore } = this.state;
        const {
            content: { start, length, includeScoreLabels, options = [] }
        } = this.props;
        const labels = options.reduce((labels, option) => {
            labels[option.score] = includeScoreLabels ? option.score : option.label;
            return labels;
        }, {});

        const className = cns('likertSlide-slider', {
            'likertSlide-slider-includeScore': includeScoreLabels
        });

        return (
            <div className={className}>
                <Slider
                    min={start}
                    max={start + length - 1}
                    value={selectedScore}
                    onChange={this.handleOptionSelect}
                    tooltip={false}
                    labels={labels}
                />
            </div>
        );
    };

    getOptions = () => {
        const {
            content: { displayType, options = [] }
        } = this.props;
        if (displayType === 'slider') {
            return this.getSlider();
        }
        return options.map(this.getOption);
    };

    render() {
        const {
            currentUser,
            name,
            nextSectionName,
            content: { description = '' },
            sectionId,
            sectionItemId,
            hideBookmark,
            sendWorkspaceSegementEvent,
            isRefSheet = false,
        } = this.props;

        return (
            <div className={'likertSlide'}>
                <WorkspaceSlideHeader
                    headerText={name}
                    currentSectionItemId={sectionItemId}
                    currentSectionId={sectionId}
                    currentUser={currentUser}
                    hideBookmark={hideBookmark}
                    isRefSheet={isRefSheet}
                />
                <div className={'playerContent'}>
                    <RichTextDisplay
                        className={'playerDescription'}
                        htmlToDisplay={description}
                        currentUser={currentUser}
                        nextSectionName={nextSectionName}
                    />
                    <div className={'likertSlide-options'}>{this.getOptions()}</div>
                </div>
            </div>
        );
    }
}

export const LikertSlideComponent = LikertSlide;
export default TrackPlayerSlideComponent(LikertSlide);
